/**
 * Inventory Optimizer — five collapsible tiles + a full-detail collapse.
 *
 * Each section renders as a tile that shows its headline KPI when
 * collapsed; clicking the tile expands the full table inline. All tiles
 * are collapsed by default so the page lands on a scannable summary.
 *
 *   1. Purchase Plan        — items below recommended min, sorted by $
 *   2. Top Fast Movers      — highest consumption in the window
 *   3. Min/Max Recommendations — items whose configured min/max drifted
 *                                from the recommendation past the threshold
 *   4. Excess Inventory     — items above recommended max, sorted by $
 *   5. Non-Critical Stagnant — items with on-hand but zero consumption,
 *                              not flagged critical (potential dead stock)
 *
 * One data fetch powers all five tiles — we slice / sort / cap a single
 * OptimizerReport in the browser. Shared controls (date range, granularity,
 * workcenter) sit at the top; the full per-row detail is
 * still a separate collapse at the bottom for power users.
 */
import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtWorkCenter } from "../../lib/format";
import {
  type OptimizerGranularity,
  type OptimizerLine,
  type OptimizerReport,
  fetchInventoryOptimizer,
} from "../../services/reportsApi";

/** Plain-text location string for spreadsheet cells (no JSX). Matches the
 *  granularity-driven Location column in the on-page tables. */
function locText(l: OptimizerLine, granularity: OptimizerGranularity): string {
  if (granularity === "fleet") return "(fleet)";
  if (granularity === "smartbox") {
    const primary = l.smartbox_code ?? l.scu_name ?? "";
    const wc = fmtWorkCenter(l.workcenter_name) ?? "";
    return wc ? `${primary} · ${wc}` : primary;
  }
  return fmtWorkCenter(l.workcenter_name) ?? "";
}

const fmt2 = (n: number | null | undefined): string =>
  n == null ? "—" : n.toFixed(2);

const fmtMoney = (n: number | null | undefined): string =>
  n == null ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const TOP_N_PURCHASE = 50;
const TOP_N_FAST = 10;
const TOP_N_EXCESS = 50;
const TOP_N_STAGNANT = 50;

interface LocationOption {
  value: string;
  label: string;
}

interface Props {
  locations: LocationOption[];
}

export const InventoryOptimizerSection: React.FC<Props> = ({ locations }) => {
  const { token } = useAuth();
  const [granularity, setGranularity] = useState<OptimizerGranularity>("workcenter");
  const [workcenter, setWorkcenter] = useState<string>("");
  const [since, setSince] = useState<string>(isoDaysAgo(30));
  const [until, setUntil] = useState<string>(isoToday());
  const [report, setReport] = useState<OptimizerReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullTable, setShowFullTable] = useState(false);

  const run = (): void => {
    if (!token) return;
    setLoading(true);
    setError(null);
    setReport(null);
    fetchInventoryOptimizer(token, {
      granularity,
      workcenter: workcenter || null,
      since,
      until,
    })
      .then(setReport)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  };

  // No auto-run on mount — the optimizer is an expensive query and most
  // visits to the Reports page don't want it. The user clicks "Generate
  // report" to fire the first run; subsequent clicks become "Refresh".

  // --- Derived section views -------------------------------------------------

  const lines = report?.lines ?? [];

  const purchasePlan = useMemo(
    () =>
      [...lines]
        .filter((l) => l.refill_qty > 0)
        .sort(
          (a, b) =>
            (b.replenishment_cost ?? b.refill_qty) -
            (a.replenishment_cost ?? a.refill_qty),
        )
        .slice(0, TOP_N_PURCHASE),
    [lines],
  );

  const fastMovers = useMemo(
    () =>
      [...lines]
        .filter((l) => l.qty_consumed > 0)
        .sort((a, b) => b.qty_consumed - a.qty_consumed)
        .slice(0, TOP_N_FAST),
    [lines],
  );

  const minMaxRecs = useMemo(
    () =>
      [...lines]
        .filter((l) => l.update_recommended)
        .sort((a, b) => {
          // Sort by absolute relative change so the biggest drift surfaces first.
          const aMinDrift =
            a.min_qty && a.min_qty > 0
              ? Math.abs(a.recommended_min - a.min_qty) / a.min_qty
              : 0;
          const bMinDrift =
            b.min_qty && b.min_qty > 0
              ? Math.abs(b.recommended_min - b.min_qty) / b.min_qty
              : 0;
          return bMinDrift - aMinDrift;
        }),
    [lines],
  );

  const excess = useMemo(
    () =>
      [...lines]
        .filter((l) => l.excess_qty > 0)
        .sort(
          (a, b) =>
            (b.value_of_excess ?? b.excess_qty) -
            (a.value_of_excess ?? a.excess_qty),
        )
        .slice(0, TOP_N_EXCESS),
    [lines],
  );

  const stagnant = useMemo(
    () =>
      [...lines]
        .filter(
          (l) => l.qty_consumed === 0 && l.on_hand > 0 && !l.is_critical,
        )
        .sort((a, b) => {
          // Value-weighted: items with a known unit price sort first by
          // (on_hand * price); items without a price by on_hand alone.
          const av = (a.unit_price ?? 0) * a.on_hand;
          const bv = (b.unit_price ?? 0) * b.on_hand;
          if (av !== bv) return bv - av;
          return b.on_hand - a.on_hand;
        })
        .slice(0, TOP_N_STAGNANT),
    [lines],
  );

  // --- KPI totals ------------------------------------------------------------

  const totalPurchaseCost = lines.reduce(
    (s, l) => s + (l.replenishment_cost ?? 0),
    0,
  );
  const totalExcessValue = lines.reduce(
    (s, l) => s + (l.value_of_excess ?? 0),
    0,
  );
  const totalStagnantValue = stagnant.reduce(
    (s, l) => s + (l.unit_price ?? 0) * l.on_hand,
    0,
  );
  const activeUpdates = minMaxRecs.length;

  // --- Excel export — one sheet per section + a Full Detail sheet -----------
  //
  // Each on-page tile becomes a workbook tab with its own targeted column
  // set, so analysts see the same columns in Excel that they see in the UI
  // instead of a single sprawling CSV. `Full detail` mirrors the old CSV
  // shape so power users still have every field in one place.

  const exportXlsx = (): void => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    const round2 = (n: number | null | undefined): number | string =>
      n == null ? "" : Number(n.toFixed(2));
    const round4 = (n: number | null | undefined): number | string =>
      n == null ? "" : Number(n.toFixed(4));

    // Purchase Plan tab.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        purchasePlan.map((l) => ({
          "Item":             l.sku ?? "",
          "Description":      l.name ?? "",
          "Location":         locText(l, granularity),
          "On hand":          l.on_hand,
          "Avg/day":          round2(l.avg_daily_consumption),
          "Days OH":          round2(l.days_on_hand),
          "Refill qty":       l.refill_qty,
          "Refill cost":      round2(l.replenishment_cost),
        })),
      ),
      "Purchase Plan",
    );

    // Top Fast Movers tab.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        fastMovers.map((l) => ({
          "Item":             l.sku ?? "",
          "Description":      l.name ?? "",
          "Location":         locText(l, granularity),
          "On hand":          l.on_hand,
          "Rec Max":          l.recommended_max,
          "Days OH":          round2(l.days_on_hand),
          "Consumed":         l.qty_consumed,
          "Avg/day":          round2(l.avg_daily_consumption),
        })),
      ),
      "Fast Movers",
    );

    // Min/Max Recommendations tab.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        minMaxRecs.map((l) => ({
          "Item":             l.sku ?? "",
          "Description":      l.name ?? "",
          "Location":         locText(l, granularity),
          "Min":              l.min_qty ?? "",
          "Rec Min":          l.recommended_min,
          "Max":              l.suggest_qty ?? "",
          "Rec Max":          l.recommended_max,
          "Consumed":         l.qty_consumed,
        })),
      ),
      "Min-Max Recs",
    );

    // Excess Inventory tab.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        excess.map((l) => ({
          "Item":             l.sku ?? "",
          "Description":      l.name ?? "",
          "Location":         locText(l, granularity),
          "On hand":          l.on_hand,
          "Rec Max":          l.recommended_max,
          "Excess qty":       l.excess_qty,
          "Excess value":     round2(l.value_of_excess),
        })),
      ),
      "Excess",
    );

    // Stagnant Inventory tab.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        stagnant.map((l) => ({
          "Item":             l.sku ?? "",
          "Description":      l.name ?? "",
          "Location":         locText(l, granularity),
          "On hand":          l.on_hand,
          "Unit price":       round2(l.unit_price),
          "Stagnant value":   l.unit_price != null ? round2(l.unit_price * l.on_hand) : "",
        })),
      ),
      "Stagnant",
    );

    // Full Detail tab — the old CSV shape, verbatim.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        lines.map((l) => ({
          "Analyze?":                    l.on_hand === 0 && l.qty_consumed === 0 ? "N" : "Y",
          "ITEM NO.":                    l.sku ?? "",
          "DESCRIPTION":                 l.name ?? "",
          "SCU":                         l.scu_name ?? "",
          "Smartbox":                    l.smartbox_code ?? "",
          "WorkCenter":                  l.workcenter_name ?? "",
          "Lead Time (days)":            l.lead_time_days,
          "Critical?":                   l.is_critical ? "Y" : "N",
          "Quantity Consumed":           l.qty_consumed,
          "Avg Daily Consumption":       round4(l.avg_daily_consumption),
          "Quantity on Hand":            l.on_hand,
          "Days on Hand":                round2(l.days_on_hand),
          "Min":                         l.min_qty ?? "",
          "Max":                         l.suggest_qty ?? "",
          "Target Days On Hand - Min":   l.target_days_min,
          "Target Days On Hand - Max":   l.target_days_max,
          "Recommended Min":             l.recommended_min,
          "Recommended Max":             l.recommended_max,
          "Refill Quantity":             l.refill_qty,
          "Excess Quantity On Hand":     l.excess_qty,
          "Unit Price":                  round2(l.unit_price),
          "Value of Excess":             round2(l.value_of_excess),
          "Replenishment Cost":          round2(l.replenishment_cost),
          "Update Min/Max?":             l.update_recommended ? "Y" : "N",
        })),
      ),
      "Full Detail",
    );

    // XLSX.writeFile handles the download blob + click + revoke internally.
    XLSX.writeFile(wb, `inventory-optimizer-${isoToday()}.xlsx`);
  };

  return (
    <div className="space-y-4">
      {/* Shared controls */}
      <div className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3">
        <label className="text-xs text-secondary-600">
          <div>Since</div>
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-secondary-600">
          <div>Until</div>
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-secondary-600">
          <div>Granularity</div>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as OptimizerGranularity)}
            className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="fleet">Fleet-wide</option>
            <option value="workcenter">Warehouse</option>
            <option value="smartbox">Smartbox</option>
          </select>
        </label>
        <label className="text-xs text-secondary-600">
          <div>Workcenter</div>
          <select
            value={workcenter}
            onChange={(e) => setWorkcenter(e.target.value)}
            className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">— All —</option>
            {locations.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {loc.label}
              </option>
            ))}
          </select>
        </label>
        <Button onClick={run} disabled={loading}>
          {loading
            ? "Generating report…"
            : report
              ? "Refresh"
              : "Generate report"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!report || loading}>
          Export Excel
        </Button>
      </div>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {!report && !loading && !error && (
        <div className="text-sm text-secondary-600 bg-white p-6 rounded-md border border-secondary-200">
          Pick a date range, granularity, and (optional) workcenter above,
          then click <strong>Generate report</strong>. The query touches
          consumption history + every visible smartbox's latest scan, so it
          takes a few seconds.
        </div>
      )}

      {report && (
        <div className="text-xs text-secondary-500">
          {report.since} → {report.until} · {report.granularity} · consumption =
          confirmed departures − re-arrivals · <strong>{lines.length}</strong> rows analyzed
        </div>
      )}

      {/* Tiles — each section is a collapsible card whose header shows the
          headline KPI. All start collapsed so the page lands on a summary. */}
      {report && (
        <>
          <SectionTile
            title="Purchase Plan"
            kpi={fmtMoney(totalPurchaseCost)}
            kpiLabel="recommended purchase cost"
            tone={totalPurchaseCost > 0 ? "warn" : "ok"}
            subtitle={`${purchasePlan.length} item${purchasePlan.length === 1 ? "" : "s"} below recommended min`}
            empty="No items need refilling in the selected window."
            rows={purchasePlan}
            columns={[
              { header: "Item", accessor: itemCell },
              { header: "Location", accessor: locCell(granularity) },
              { header: "On hand", align: "right", accessor: (l) => l.on_hand },
              { header: "Avg/day", align: "right", accessor: (l) => fmt2(l.avg_daily_consumption) },
              { header: "Days OH", align: "right", accessor: (l) => fmt2(l.days_on_hand) },
              { header: "Refill qty", align: "right", accessor: (l) => <strong>{l.refill_qty}</strong> },
              { header: "Refill cost", align: "right", accessor: (l) => fmtMoney(l.replenishment_cost) },
            ]}
          />

          <SectionTile
            title="Top Fast Movers"
            kpi={String(fastMovers.length)}
            kpiLabel="items"
            tone="info"
            subtitle={`Top ${fastMovers.length} items by total consumption in the window`}
            empty="No consumption events recorded in this window."
            rows={fastMovers}
            columns={[
              { header: "Item", accessor: itemCell },
              { header: "Location", accessor: locCell(granularity) },
              { header: "On hand", align: "right", accessor: (l) => l.on_hand },
              { header: "Rec Max", align: "right", accessor: (l) => l.recommended_max },
              { header: "Days OH", align: "right", accessor: (l) => fmt2(l.days_on_hand) },
              { header: "Consumed", align: "right", accessor: (l) => <strong>{l.qty_consumed}</strong> },
              { header: "Avg/day", align: "right", accessor: (l) => fmt2(l.avg_daily_consumption) },
            ]}
          />

          <SectionTile
            title="Min/Max Recommendations"
            kpi={String(activeUpdates)}
            kpiLabel="updates suggested"
            tone={activeUpdates > 0 ? "info" : "ok"}
            subtitle={`Items where current min/max drifted past the configured threshold`}
            empty="Configured min/max levels are within tolerance of the recommendation."
            rows={minMaxRecs}
            columns={[
              { header: "Item", accessor: itemCell },
              { header: "Location", accessor: locCell(granularity) },
              { header: "Min", align: "right", accessor: (l) => l.min_qty ?? "—" },
              { header: "Rec Min", align: "right", accessor: (l) => <strong>{l.recommended_min}</strong> },
              { header: "Max", align: "right", accessor: (l) => l.suggest_qty ?? "—" },
              { header: "Rec Max", align: "right", accessor: (l) => <strong>{l.recommended_max}</strong> },
              { header: "Consumed", align: "right", accessor: (l) => l.qty_consumed },
            ]}
          />

          <SectionTile
            title="Excess Inventory"
            kpi={fmtMoney(totalExcessValue)}
            kpiLabel="value above max"
            tone={totalExcessValue > 0 ? "warn" : "ok"}
            subtitle={`${excess.length} item${excess.length === 1 ? "" : "s"} above recommended max`}
            empty="No items currently above their recommended max."
            rows={excess}
            columns={[
              { header: "Item", accessor: itemCell },
              { header: "Location", accessor: locCell(granularity) },
              { header: "On hand", align: "right", accessor: (l) => l.on_hand },
              { header: "Rec Max", align: "right", accessor: (l) => l.recommended_max },
              { header: "Excess qty", align: "right", accessor: (l) => <strong>{l.excess_qty}</strong> },
              { header: "Excess value", align: "right", accessor: (l) => fmtMoney(l.value_of_excess) },
            ]}
          />

          <SectionTile
            title="Stagnant Inventory"
            kpi={fmtMoney(totalStagnantValue)}
            kpiLabel="non-critical, no consumption"
            tone={stagnant.length > 0 ? "warn" : "ok"}
            subtitle={`${stagnant.length} item${stagnant.length === 1 ? "" : "s"} with on-hand stock but zero consumption in the window`}
            empty="No stagnant inventory. Every on-hand item moved in this window."
            rows={stagnant}
            columns={[
              { header: "Item", accessor: itemCell },
              { header: "Location", accessor: locCell(granularity) },
              { header: "On hand", align: "right", accessor: (l) => <strong>{l.on_hand}</strong> },
              {
                header: "Stagnant value",
                align: "right",
                accessor: (l) =>
                  fmtMoney(
                    l.unit_price != null ? l.unit_price * l.on_hand : null,
                  ),
              },
            ]}
          />

          {/* Full per-row detail — useful but heavy. Collapse by default. */}
          <section className="bg-white border border-secondary-200 rounded-md">
            <button
              type="button"
              onClick={() => setShowFullTable((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-secondary-800 hover:bg-secondary-50"
              aria-expanded={showFullTable}
            >
              <span>Full detail ({lines.length} rows)</span>
              <span>{showFullTable ? "▾" : "▸"}</span>
            </button>
            {showFullTable && (
              <FullDetailTable lines={lines} granularity={granularity} />
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default InventoryOptimizerSection;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface Column {
  header: string;
  align?: "left" | "right";
  accessor: (l: OptimizerLine) => React.ReactNode;
}

/**
 * Collapsible tile. The header shows the section title and a prominent
 * headline KPI; the table body is hidden until the user clicks the
 * header. Each tile owns its own expanded state so opening one doesn't
 * close another — all start collapsed so the page lands on a summary.
 */
const SectionTile: React.FC<{
  title: string;
  kpi: React.ReactNode;
  kpiLabel: string;
  tone?: "ok" | "info" | "warn";
  subtitle?: React.ReactNode;
  rows: OptimizerLine[];
  columns: Column[];
  empty: string;
}> = ({ title, kpi, kpiLabel, tone = "info", subtitle, rows, columns, empty }) => {
  const [expanded, setExpanded] = useState(false);

  const kpiTone =
    tone === "warn"
      ? "text-warning-700"
      : tone === "ok"
        ? "text-success-700"
        : "text-secondary-900";
  const ringTone =
    tone === "warn"
      ? "border-warning-200"
      : tone === "ok"
        ? "border-success-200"
        : "border-secondary-200";

  return (
    <section className={"bg-white border rounded-md " + ringTone}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary-50 text-left"
      >
        <span
          className="text-secondary-500 text-sm w-4 flex-shrink-0"
          aria-hidden
        >
          {expanded ? "▾" : "▸"}
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-secondary-900">{title}</h2>
          {subtitle && (
            <p className="text-xs text-secondary-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className={"text-xl font-bold font-mono " + kpiTone}>{kpi}</div>
          <div className="text-[11px] uppercase tracking-wide text-secondary-500">
            {kpiLabel}
          </div>
        </div>
      </button>
      {expanded && (
        rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-secondary-500 bg-success-50 border-t border-secondary-100">
            ✓ {empty}
          </div>
        ) : (
          <div className="overflow-x-auto border-t border-secondary-100">
            <table className="min-w-full text-xs">
              <thead className="bg-secondary-50 text-secondary-700 uppercase">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.header}
                      className={
                        "px-3 py-2 whitespace-nowrap " +
                        (c.align === "right" ? "text-right" : "text-left")
                      }
                    >
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {rows.map((l, i) => (
                  <tr
                    key={`${l.item_id}-${l.scu_name ?? l.workcenter_name ?? "fleet"}-${i}`}
                    className="hover:bg-secondary-50"
                  >
                    {columns.map((c) => (
                      <td
                        key={c.header}
                        className={
                          "px-3 py-1.5 align-top font-mono " +
                          (c.align === "right" ? "text-right" : "text-left")
                        }
                      >
                        {c.accessor(l)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
};

const itemCell = (l: OptimizerLine): React.ReactNode => (
  <div>
    <div className="font-semibold">
      {l.sku ?? "—"}
      {l.is_critical && (
        <span
          className="ml-1 text-error-700 font-bold"
          title="Flagged critical — recommendations preserve current min/max"
        >
          ●
        </span>
      )}
    </div>
    {l.name && (
      <div className="text-secondary-500 text-[10px] truncate max-w-[280px] font-sans">
        {l.name}
      </div>
    )}
  </div>
);

const locCell =
  (granularity: OptimizerGranularity) =>
  (l: OptimizerLine): React.ReactNode => {
    if (granularity === "fleet") {
      return <span className="text-secondary-400 font-sans">(fleet)</span>;
    }
    if (granularity === "smartbox") {
      return (
        <div className="font-sans">
          <div className="text-secondary-900">
            {l.smartbox_code ?? l.scu_name ?? "—"}
          </div>
          <div className="text-secondary-500 text-[10px]">
            {fmtWorkCenter(l.workcenter_name) || ""}
          </div>
        </div>
      );
    }
    return <span className="font-sans">{fmtWorkCenter(l.workcenter_name) || "—"}</span>;
  };

const FullDetailTable: React.FC<{
  lines: OptimizerLine[];
  granularity: OptimizerGranularity;
}> = ({ lines, granularity }) => {
  return (
  <div className="overflow-x-auto border-t border-secondary-100">
    <table className="min-w-full text-xs">
      <thead className="bg-secondary-50 text-secondary-700 uppercase">
        <tr>
          <th className="text-left px-2 py-1.5">Item</th>
          {granularity !== "fleet" && (
            <th className="text-left px-2 py-1.5">Location</th>
          )}
          <th className="text-left px-2 py-1.5">Lead</th>
          <th className="text-left px-2 py-1.5">Crit</th>
          <th className="text-right px-2 py-1.5">Consumed</th>
          <th className="text-right px-2 py-1.5">Avg/day</th>
          <th className="text-right px-2 py-1.5">On hand</th>
          <th className="text-right px-2 py-1.5">Days OH</th>
          <th className="text-right px-2 py-1.5">Min</th>
          <th className="text-right px-2 py-1.5">Max</th>
          <th className="text-right px-2 py-1.5">Rec Min</th>
          <th className="text-right px-2 py-1.5">Rec Max</th>
          <th className="text-right px-2 py-1.5">Refill</th>
          <th className="text-right px-2 py-1.5">Excess</th>
          <th className="text-right px-2 py-1.5">Refill $</th>
          <th className="text-right px-2 py-1.5">Excess $</th>
          <th className="text-left px-2 py-1.5">Update?</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-secondary-100">
        {lines.map((l, i) => (
          <tr
            key={`${l.item_id}-${l.scu_name ?? l.workcenter_name ?? "fleet"}-${i}`}
            className="hover:bg-secondary-50"
          >
            <td className="px-2 py-1 align-top">
              <code className="font-mono font-semibold">{l.sku ?? "—"}</code>
              {l.name && (
                <div className="text-secondary-500 text-[10px] truncate max-w-[240px]">
                  {l.name}
                </div>
              )}
            </td>
            {granularity !== "fleet" && (
              <td className="px-2 py-1 align-top">
                {granularity === "smartbox" && (
                  <div>{l.smartbox_code ?? l.scu_name}</div>
                )}
                <div className="text-secondary-500 text-[10px]">
                  {fmtWorkCenter(l.workcenter_name) || "—"}
                </div>
              </td>
            )}
            <td className="px-2 py-1 align-top">{l.lead_time_days}d</td>
            <td className="px-2 py-1 align-top">
              {l.is_critical ? (
                <span className="text-error-700 font-semibold">●</span>
              ) : (
                ""
              )}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.qty_consumed}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {fmt2(l.avg_daily_consumption)}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">{l.on_hand}</td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {fmt2(l.days_on_hand)}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.min_qty ?? "—"}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.suggest_qty ?? "—"}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.recommended_min}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.recommended_max}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.refill_qty > 0 ? <strong>{l.refill_qty}</strong> : ""}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {l.excess_qty > 0 ? <strong>{l.excess_qty}</strong> : ""}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {fmtMoney(l.replenishment_cost)}
            </td>
            <td className="px-2 py-1 align-top text-right font-mono">
              {fmtMoney(l.value_of_excess)}
            </td>
            <td className="px-2 py-1 align-top">
              <span
                className={
                  l.update_recommended
                    ? "bg-warning-50 text-warning-800 border border-warning-300 px-1 rounded"
                    : "text-secondary-400"
                }
              >
                {l.update_recommended ? "Y" : "—"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};
